﻿import type { ButtonHTMLAttributes } from "react";

type ButtonVariant = "primary" | "secondary" | "danger";
type ButtonColor = "blue" | "emerald" | "amber" | "teal" | "purple" | "orange";
type ButtonSize = "sm" | "md" | "lg";

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-5 py-2.5 text-sm",
};

// `"blue"` is the default primary button color and wires to the user's
// chosen accent (see `src/styles/appearance.css`). The remaining entries
// are literal Tier A tones and double as the per-module identity colors
// for CTAs on their list pages (Sales = teal, Expenses = purple, Catalog
// = orange — see ADR 0008). `"emerald"` and `"amber"` remain available
// for non-module semantic buttons.
// Every variant carries a border (transparent on filled variants) so
// primary/secondary/danger buttons box out to the same height side-by-side.
const PRIMARY_BY_COLOR: Record<ButtonColor, string> = {
  blue: "border border-transparent bg-accent-600 text-white hover:bg-accent-700",
  emerald: "border border-transparent bg-emerald-600 text-white hover:bg-emerald-700",
  amber: "border border-transparent bg-amber-500 text-white hover:bg-amber-600",
  teal: "border border-transparent bg-teal-600 text-white hover:bg-teal-700",
  purple: "border border-transparent bg-purple-600 text-white hover:bg-purple-700",
  orange: "border border-transparent bg-orange-600 text-white hover:bg-orange-700",
};

const SECONDARY_CLASSES =
  "border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-fg-primary hover:bg-slate-50 dark:hover:bg-slate-600";

const DANGER_CLASSES =
  "border border-transparent bg-red-600 text-white hover:bg-red-700";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
};

export function Button({
  variant = "primary",
  color = "blue",
  size = "md",
  className = "",
  type = "button",
  ...rest
}: ButtonProps) {
  const variantClasses =
    variant === "primary"
      ? PRIMARY_BY_COLOR[color]
      : variant === "secondary"
        ? SECONDARY_CLASSES
        : DANGER_CLASSES;
  return (
    <button
      type={type}
      // whitespace-nowrap keeps sibling buttons the same height in tight
      // header rows (labels never wrap to two lines); the row is expected to
      // flex-wrap whole buttons instead.
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium shadow-sm transition-colors disabled:opacity-50 ${SIZE_CLASSES[size]} ${variantClasses} ${className}`.trim()}
      {...rest}
    />
  );
}
